import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';

type Variant = 'default' | 'accent' | 'primary' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  block?: boolean;
  icon?: boolean; // square icon-only button
  children?: ReactNode;
}

// Shared button primitive. Encapsulates the long token-backed utility strings so call-sites
// stay terse and every button stays visually consistent (DRY — check here before rolling a new one).
const VARIANTS: Record<Variant, string> = {
  default: 'bg-surface border-line-2 text-ink hover:bg-surface-2 hover:border-line-3',
  accent: 'bg-accent border-accent text-accent-ink hover:bg-accent-2',
  primary: 'bg-ink border-ink text-bg hover:bg-ink-2 hover:border-ink-2',
  ghost: 'bg-transparent border-transparent text-ink-2 hover:bg-surface-2 hover:text-ink',
  // Destructive action — solid danger fill on hover so the colour change reads as commitment,
  // not just hover styling. Default state stays softer (tint + danger text + danger border)
  // to avoid screaming on every list with a delete option.
  danger: 'bg-danger-tint border-danger text-danger hover:bg-danger hover:text-bg',
};

// Height + text only. Horizontal padding is kept SEPARATE (PADDING below) so an icon-only button
// can drop it entirely — emitting both `px-3.5` and `px-0` doesn't work: Tailwind's compiled CSS
// orders `.px-0` before `.px-3.5`, so the size padding wins regardless of class order, squeezing the
// icon's flex box until it shrinks to a sliver.
const SIZES: Record<Size, string> = {
  sm: 'h-[30px] text-xs',
  md: 'h-9 text-[13px]',
  lg: 'h-11 text-sm',
};

const PADDING: Record<Size, string> = {
  sm: 'px-2.5',
  md: 'px-3.5',
  lg: 'px-[18px]',
};

// forwardRef because Radix composes this: `AlertDialogTrigger asChild` (and Cancel/Action) clone
// their child and hand it a ref to drive focus management and outside-click detection. A plain
// function component silently drops that ref — React warns, and Radix loses the focus handling a
// modal dialog depends on.
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'default',
    size = 'md',
    block = false,
    icon = false,
    className = '',
    type = 'button',
    children,
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={[
        'inline-flex items-center gap-2 rounded-btn border-[1.5px] font-medium whitespace-nowrap',
        'transition-colors active:translate-y-[0.5px] disabled:opacity-50 disabled:pointer-events-none',
        VARIANTS[variant],
        SIZES[size],
        // Icon-only buttons take no horizontal padding (square); everyone else gets the size's.
        icon ? 'justify-center aspect-square' : PADDING[size],
        block ? 'w-full justify-center' : '',
        className,
      ].join(' ')}
      {...rest}
    >
      {children}
    </button>
  );
});
